import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AiAgentService, { CHAVES_CHAT } from '../api/AiAgentService';
import { useAiAgentToggle } from '../context/AiAgentContext';
import { ChatMessage, Routine } from '../types/entities';

/** Referência estável: `?? []` inline criaria um array novo a cada render e o
 *  effect de scroll dispararia sempre. */
const SEM_MENSAGENS: ChatMessage[] = [];

export default function useAiAgent() {
  const { isOpen, pendingPrompt, consumePrompt } = useAiAgentToggle();
  const queryClient = useQueryClient();

  const [input, setInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Rotinas recomendadas pela IA na última resposta — abrem o modal de revisão.
  const [suggestedRoutines, setSuggestedRoutines] = useState<Routine[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Histórico só busca quando o chat abre; reabrir dentro da janela de cache não
  // refaz a requisição. As mensagens novas entram por `setQueryData`.
  const historicoQuery = useQuery({
    queryKey: CHAVES_CHAT.historico,
    queryFn: () => AiAgentService.getHistory(),
    enabled: isOpen,
    staleTime: Infinity,
  });

  const messages = historicoQuery.data ?? SEM_MENSAGENS;

  const acrescentar = (msg: ChatMessage) =>
    queryClient.setQueryData<ChatMessage[]>(CHAVES_CHAT.historico, (prev) => [...(prev ?? []), msg]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (texto?: string) => {
    const trimmed = (texto ?? input).trim();
    if (!trimmed || loading) return;

    acrescentar({ role: 'user', content: trimmed, createdAt: new Date().toISOString() });
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await AiAgentService.chat(trimmed);
      acrescentar({ role: 'model', content: response.message, createdAt: response.createdAt });
      if (response.suggestedRoutines?.length) setSuggestedRoutines(response.suggestedRoutines);
    } catch {
      setError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Pergunta vinda de fora (ex.: "Ver ajuste"): dispara sozinha assim que o
  // histórico terminou de carregar — antes disso o fetch sobrescreveria a bolha.
  const historyLoaded = historicoQuery.isSuccess || historicoQuery.isError;
  useEffect(() => {
    if (!isOpen || !pendingPrompt || !historyLoaded) return;
    consumePrompt();
    sendMessage(pendingPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingPrompt, historyLoaded]);

  const clearHistory = async () => {
    try {
      await AiAgentService.clearHistory();
      queryClient.setQueryData<ChatMessage[]>(CHAVES_CHAT.historico, []);
    } catch {
      setError('Não foi possível limpar o histórico.');
    }
  };

  return {
    messages,
    input,
    setInput,
    loading,
    // Erro também conta como "carregou": a tela mostra o chat vazio, não um spinner eterno.
    historyLoaded,
    error,
    bottomRef,
    sendMessage,
    clearHistory,
    suggestedRoutines,
    dismissSuggestion: () => setSuggestedRoutines([]),
  };
}
